import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import {
  ArrowLeft,
  BadgeCheck,
  Camera,
  CreditCard,
  Download,
  ImagePlus,
  MessageCircle,
  QrCode,
  ShieldCheck,
  Sparkles,
  Ticket,
  Utensils,
  Wifi,
  Zap,
} from "lucide-react";
import "./App.css";
import { LEGAL_CONTENT } from "./constants/legal";

type View = "home" | "about" | "privacy" | "privacyTech" | "terms" | "blogQr2026";
type ContentMode = "url" | "text" | "whatsapp" | "wifi" | "pix";
type DownloadFormat = "png" | "jpeg" | "svg";
type DotStyle =
  | "square"
  | "dots"
  | "rounded"
  | "classy"
  | "classy-rounded"
  | "extra-rounded";
type CornerSquareStyle = "square" | "dot" | "extra-rounded";
type CornerDotStyle = "square" | "dot";
type WifiSecurity = "WPA" | "WEP" | "nopass";

type QrFormState = {
  mode: ContentMode;
  url: string;
  text: string;
  whatsappPhone: string;
  whatsappMessage: string;
  wifiSsid: string;
  wifiPassword: string;
  wifiSecurity: WifiSecurity;
  wifiHidden: boolean;
  pixKey: string;
  pixName: string;
  pixCity: string;
  pixAmount: string;
  pixDescription: string;
  pixTxid: string;
};

type QrStyleState = {
  foreground: string;
  background: string;
  dotStyle: DotStyle;
  cornerSquareStyle: CornerSquareStyle;
  cornerDotStyle: CornerDotStyle;
};

const SITE_NAME = "QR Forge";
const PAGE_TITLE = "Gerador de QR Code com Logo, Pix, Wi-Fi e WhatsApp";
const PAGE_DESCRIPTION =
  "Crie QR Codes personalizados com logo, download em PNG, JPG e SVG, presets rápidos para Pix, Wi-Fi e WhatsApp, além de conteúdo otimizado para SEO.";

const DEFAULT_FORM: QrFormState = {
  mode: "url",
  url: "https://instagram.com/sua-marca",
  text: "Bem-vindo ao QR Forge",
  whatsappPhone: "5511999999999",
  whatsappMessage: "Olá! Vim pelo seu QR Code.",
  wifiSsid: "MinhaRedeWiFi",
  wifiPassword: "senha12345",
  wifiSecurity: "WPA",
  wifiHidden: false,
  pixKey: "contato@suaempresa.com.br",
  pixName: "SUA EMPRESA",
  pixCity: "SAO PAULO",
  pixAmount: "49.90",
  pixDescription: "Pagamento QR",
  pixTxid: "QRPIX01",
};

const DEFAULT_STYLE: QrStyleState = {
  foreground: "#4f46e5",
  background: "#ffffff",
  dotStyle: "rounded",
  cornerSquareStyle: "extra-rounded",
  cornerDotStyle: "dot",
};

const INSTAGRAM_ICON = encodeSvgDataUrl(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" fill="none">
    <defs>
      <linearGradient id="ig" x1="12" y1="84" x2="84" y2="12" gradientUnits="userSpaceOnUse">
        <stop stop-color="#F9CE34"/>
        <stop offset="0.45" stop-color="#EE2A7B"/>
        <stop offset="1" stop-color="#6228D7"/>
      </linearGradient>
    </defs>
    <rect x="8" y="8" width="80" height="80" rx="22" fill="url(#ig)"/>
    <rect x="25" y="25" width="46" height="46" rx="14" stroke="white" stroke-width="7"/>
    <circle cx="48" cy="48" r="12" stroke="white" stroke-width="7"/>
    <circle cx="67" cy="29" r="5" fill="white"/>
  </svg>
`);

const FAQ_ITEMS = [
  {
    question: "Como colocar uma logo no centro do QR Code?",
    answer:
      "Use o botão de upload para enviar a logo da marca ou selecione o ícone do Instagram. O app aplica correção de erro alta para manter boa leitura mesmo com o centro personalizado.",
  },
  {
    question: "Posso baixar o QR Code em SVG?",
    answer:
      "Sim. O download em SVG gera um arquivo vetorial, ideal para designers, gráficas e materiais que exigem redimensionamento sem perda de qualidade.",
  },
  {
    question: "O QR Code de Pix funciona com chave real?",
    answer:
      "Sim. O formulário monta o payload Pix com chave, nome, cidade, valor opcional, descrição e CRC, pronto para leitura em apps bancários compatíveis.",
  },
  {
    question: "Os dados enviados ficam salvos em servidor?",
    answer:
      "Não. A geração acontece no navegador do usuário. O conteúdo digitado e a logo carregada ficam apenas na sessão local.",
  },
  {
    question: 'Como funciona o QR Code para "texto"?',
    answer:
      "Ao escolher a opção de texto, você pode digitar qualquer mensagem, nota ou senha. Quando alguém escanear esse código, o conteúdo aparecerá instantaneamente na tela do celular, sem precisar de internet ou abrir um site.",
  },
];

const VIEW_PATHS: Record<View, string> = {
  home: "/",
  about: "/sobre",
  privacy: "/privacidade",
  terms: "/termos",
  privacyTech: "/privacidade-tecnica",
  blogQr2026: "/blog/importancia-dos-qr-codes-em-2026",
};

function normalizePathname(pathname: string) {
  const clean = pathname.split("?")[0]?.split("#")[0] ?? "/";
  if (!clean.startsWith("/")) return `/${clean}`;
  return clean.length > 1 ? clean.replace(/\/+$/, "") : "/";
}

function viewFromLocation(): View {
  const normalized = normalizePathname(window.location.pathname);
  const entry = (Object.entries(VIEW_PATHS) as Array<[View, string]>).find(
    ([, path]) => path === normalized,
  );
  if (entry) return entry[0];

  // Back-compat with old hash navigation.
  const legacyHash = window.location.hash.replace("#", "");
  if (legacyHash === "about") return "about";
  if (legacyHash === "privacy") return "privacy";
  if (legacyHash === "terms") return "terms";
  if (legacyHash === "privacyTech" || legacyHash === "privacidade-tecnica")
    return "privacyTech";
  if (legacyHash === "blogQr2026") return "blogQr2026";

  return "home";
}

function navigateTo(view: View) {
  const nextPath = VIEW_PATHS[view];
  if (window.location.pathname !== nextPath) {
    window.history.pushState({ view }, "", nextPath);
  }
  if (window.location.hash) {
    window.history.replaceState({ view }, "", nextPath);
  }
}

function App() {
  const [currentView, setCurrentView] = useState<View>(() => viewFromLocation());
  const [form, setForm] = useState<QrFormState>(DEFAULT_FORM);
  const [style, setStyle] = useState<QrStyleState>(DEFAULT_STYLE);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [logoName, setLogoName] = useState("");
  const [isInstagramLogo, setIsInstagramLogo] = useState(false);
  const [isDownloading, setIsDownloading] = useState<DownloadFormat | null>(
    null,
  );
  const qrContainerRef = useRef<HTMLDivElement | null>(null);
  const qrCodeRef = useRef<import("qr-code-styling").default | null>(null);
  const qrCodeLastAppendedTo = useRef<HTMLDivElement | null>(null);
  const [isQrLibReady, setIsQrLibReady] = useState(false);

  const qrData = buildQrPayload(form);
  const activeLogo = isInstagramLogo ? INSTAGRAM_ICON : logoDataUrl;
  const hasCenterLogo = Boolean(activeLogo);

  const pageMeta = useMemo(() => {
    if (currentView === "home") {
      return {
        title: `${PAGE_TITLE} | ${SITE_NAME}`,
        description: PAGE_DESCRIPTION,
        canonical: "https://qrforge.com.br/",
        ogUrl: "https://qrforge.com.br/",
      };
    }

    const titleSuffix: Record<View, string> = {
      home: PAGE_TITLE,
      about: "Sobre o QR Forge",
      privacy: "Política de Privacidade",
      terms: "Termos de Uso",
      privacyTech: "Por que a Privacidade Importa?",
      blogQr2026: "A importância dos QR Codes em 2026",
    };

    const path = VIEW_PATHS[currentView];
    const canonical = `https://qrforge.com.br${path}`;
    return {
      title: `${titleSuffix[currentView]} | ${SITE_NAME}`,
      description: PAGE_DESCRIPTION,
      canonical,
      ogUrl: canonical,
    };
  }, [currentView]);

  useEffect(() => {
    document.title = pageMeta.title;
    upsertMetaTag("description", pageMeta.description);
    upsertMetaTag(
      "keywords",
      "gerador de qr code, qr code com logo, qr code pix, qr code wifi, qr code whatsapp, qr code svg",
    );
    upsertMetaProperty("og:title", pageMeta.title);
    upsertMetaProperty("og:description", pageMeta.description);
    upsertMetaProperty("og:type", "website");
    upsertMetaProperty("og:url", pageMeta.ogUrl);
    upsertLinkTag("canonical", pageMeta.canonical);
  }, [pageMeta]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (qrCodeRef.current) return;
      const mod = await import("qr-code-styling");
      if (cancelled) return;
      const QRCodeStyling = mod.default;
      qrCodeRef.current = new QRCodeStyling({
        width: 320,
        height: 320,
        margin: 12,
        qrOptions: { errorCorrectionLevel: "H" },
        imageOptions: {
          hideBackgroundDots: true,
          imageSize: 0.24,
          margin: 8,
          saveAsBlob: true,
        },
        dotsOptions: {
          color: DEFAULT_STYLE.foreground,
          type: DEFAULT_STYLE.dotStyle,
        },
        cornersSquareOptions: {
          type: DEFAULT_STYLE.cornerSquareStyle,
          color: DEFAULT_STYLE.foreground,
        },
        cornersDotOptions: {
          type: DEFAULT_STYLE.cornerDotStyle,
          color: DEFAULT_STYLE.foreground,
        },
        backgroundOptions: { color: DEFAULT_STYLE.background },
        data: DEFAULT_FORM.url,
      });
      setIsQrLibReady(true);
    };

    const anyWindow = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
    };

    if (anyWindow.requestIdleCallback) {
      anyWindow.requestIdleCallback(() => void init(), { timeout: 1200 });
    } else {
      setTimeout(() => void init(), 0);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const syncFromLocation = () => setCurrentView(viewFromLocation());
    window.addEventListener("popstate", syncFromLocation);
    window.addEventListener("hashchange", syncFromLocation);
    return () => {
      window.removeEventListener("popstate", syncFromLocation);
      window.removeEventListener("hashchange", syncFromLocation);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    navigateTo(currentView);
  }, [currentView]);

  useEffect(() => {
    if (!isQrLibReady) return;
    if (!qrContainerRef.current) return;
    if (!qrCodeRef.current) return;

    qrCodeRef.current.update({
      data: qrData,
      image: activeLogo ?? undefined,
      imageOptions: {
        hideBackgroundDots: hasCenterLogo,
        imageSize: hasCenterLogo ? 0.24 : 0,
        margin: hasCenterLogo ? 8 : 0,
        saveAsBlob: true,
      },
      dotsOptions: { color: style.foreground, type: style.dotStyle },
      cornersSquareOptions: {
        color: style.foreground,
        type: style.cornerSquareStyle,
      },
      cornersDotOptions: {
        color: style.foreground,
        type: style.cornerDotStyle,
      },
      backgroundOptions: { color: style.background },
    });

    if (qrCodeLastAppendedTo.current !== qrContainerRef.current) {
      qrCodeLastAppendedTo.current = qrContainerRef.current;
      qrContainerRef.current.innerHTML = "";
      qrCodeRef.current.append(qrContainerRef.current);
    }
  }, [activeLogo, hasCenterLogo, isQrLibReady, qrData, style]);
  const handleLogoUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setLogoDataUrl(reader.result);
        setLogoName(file.name);
        setIsInstagramLogo(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDownload = async (extension: DownloadFormat) => {
    setIsDownloading(extension);
    try {
      if (!qrCodeRef.current) return;
      await qrCodeRef.current.download({
        name: `qr-forge-${form.mode}`,
        extension,
      });
    } finally {
      setIsDownloading(null);
    }
  };

  const applyPreset = (preset: "whatsapp" | "wifi" | "pix") => {
    if (preset === "whatsapp") {
      setForm((current) => ({
        ...current,
        mode: "whatsapp",
        whatsappPhone: "5511999999999",
        whatsappMessage: "Olá! Quero falar com sua empresa.",
      }));
      setStyle({
        foreground: "#16a34a",
        background: "#ffffff",
        dotStyle: "rounded",
        cornerSquareStyle: "extra-rounded",
        cornerDotStyle: "dot",
      });
      return;
    }

    if (preset === "wifi") {
      setForm((current) => ({
        ...current,
        mode: "wifi",
        wifiSsid: "MeuWiFi",
        wifiPassword: "internet123",
        wifiSecurity: "WPA",
        wifiHidden: false,
      }));
      setStyle({
        foreground: "#0f766e",
        background: "#ffffff",
        dotStyle: "classy-rounded",
        cornerSquareStyle: "square",
        cornerDotStyle: "square",
      });
      return;
    }

    setForm((current) => ({
      ...current,
      mode: "pix",
      pixKey: "contato@suaempresa.com.br",
      pixName: "SUA EMPRESA",
      pixCity: "SAO PAULO",
      pixAmount: "49.90",
      pixDescription: "Pagamento Pix",
      pixTxid: "PIX2026",
    }));
    setStyle({
      foreground: "#1d4ed8",
      background: "#ffffff",
      dotStyle: "dots",
      cornerSquareStyle: "extra-rounded",
      cornerDotStyle: "dot",
    });
  };

  const applyTemplate = (
    template:
      | "instagram"
      | "whatsapp"
      | "pix"
      | "wifi"
      | "cardapio"
      | "evento",
  ) => {
    if (template === "whatsapp" || template === "wifi" || template === "pix") {
      applyPreset(template);
      return;
    }

    if (template === "instagram") {
      setForm((current) => ({
        ...current,
        mode: "url",
        url: "https://instagram.com/sua-marca",
      }));
      setIsInstagramLogo(true);
      setLogoDataUrl(null);
      setLogoName("");
      return;
    }

    if (template === "cardapio") {
      setForm((current) => ({
        ...current,
        mode: "url",
        url: "https://seusite.com.br/cardapio",
      }));
      setIsInstagramLogo(false);
      return;
    }

    setForm((current) => ({
      ...current,
      mode: "url",
      url: "https://seusite.com.br/evento",
    }));
    setIsInstagramLogo(false);
  };

  const renderHome = () => (
    <>
      <section className="hero-card">
        <div className="hero-copy">
          <span className="eyebrow">
            QR premium para negócio, social e pagamento
          </span>
          <h1>Gerador de QR Code com logo, presets e download em SVG</h1>
          <p className="subtitle">
            Crie QR Codes com aparência profissional para Instagram, WhatsApp,
            Wi-Fi e Pix. Personalize cores, adicione logo central e exporte em
            formatos prontos para web, impressão e design.
          </p>

          <div className="preset-row">
            <button
              className="preset-btn"
              onClick={() => applyPreset("whatsapp")}
              aria-label="Aplicar estilo visual do WhatsApp"
            >
              Estilo WhatsApp
            </button>
            <button
              className="preset-btn"
              onClick={() => applyPreset("wifi")}
              aria-label="Aplicar estilo visual de Wi-Fi"
            >
              Estilo Wi-Fi
            </button>
            <button
              className="preset-btn"
              onClick={() => applyPreset("pix")}
              aria-label="Aplicar estilo visual do Pix"
            >
              Estilo Pix
            </button>
          </div>

          <div className="trust-row">
            <span>
              <ShieldCheck size={16} /> Processamento local
            </span>
            <span>
              <Sparkles size={16} /> Visual personalizável
            </span>
            <span>
              <Download size={16} /> PNG, JPG e SVG
            </span>
          </div>
        </div>

        <div className="hero-preview">
          <div className="preview-card">
            <div className="preview-header">
              <span>Pré-visualização ao vivo</span>
              <BadgeCheck size={16} />
            </div>
            <div ref={qrContainerRef} className="qr-stage" />
            {hasCenterLogo ? (
              <p className="scan-warning">
                Logo central ativa. Prefira artes simples, com fundo claro e
                pouco texto para preservar a leitura.
              </p>
            ) : null}
            <p className="preview-note">
              Conteúdo atual: <strong>{modeLabel(form.mode)}</strong>
            </p>
          </div>
        </div>
      </section>

      <main className="layout-grid">
        <section className="panel control-panel">
          <div className="panel-heading">
            <QrCode className="panel-icon" />
            <div>
              <h2>Conteúdo do QR Code</h2>
              <p>Escolha o tipo de destino e preencha os campos abaixo.</p>
            </div>
          </div>

          <div className="mode-tabs">
            {(["url", "text", "whatsapp", "wifi", "pix"] as ContentMode[]).map(
              (mode) => (
                <button
                  key={mode}
                  className={`mode-tab ${form.mode === mode ? "active" : ""}`}
                  onClick={() => setForm((current) => ({ ...current, mode }))}
                >
                  {modeLabel(mode)}
                </button>
              ),
            )}
          </div>

          {renderFields(form, setForm)}

          <div className="desktop-only">
            <div className="panel-divider" />

            <div className="help-stack">
              <div className="soft-card">
                <div className="soft-card-title">Comece com um modelo</div>
                <div className="soft-card-subtitle">
                  Escolha um tipo pronto para acelerar.
                </div>

                <div className="template-grid">
                  <button
                    className="template-btn"
                    onClick={() => applyTemplate("instagram")}
                  >
                    <Camera size={16} /> Instagram
                  </button>
                  <button
                    className="template-btn"
                    onClick={() => applyTemplate("whatsapp")}
                  >
                    <MessageCircle size={16} /> WhatsApp
                  </button>
                  <button
                    className="template-btn"
                    onClick={() => applyTemplate("pix")}
                  >
                    <CreditCard size={16} /> Pix
                  </button>
                  <button
                    className="template-btn"
                    onClick={() => applyTemplate("wifi")}
                  >
                    <Wifi size={16} /> Wi‑Fi
                  </button>
                  <button
                    className="template-btn"
                    onClick={() => applyTemplate("cardapio")}
                  >
                    <Utensils size={16} /> Cardápio
                  </button>
                  <button
                    className="template-btn"
                    onClick={() => applyTemplate("evento")}
                  >
                    <Ticket size={16} /> Evento
                  </button>
                </div>
              </div>

              <div className="soft-card">
                <div className="soft-card-title">Boas práticas para seu QR</div>
                <ul className="checklist">
                  <li>Alto contraste melhora leitura</li>
                  <li>Logo central pequena funciona melhor</li>
                  <li>Teste antes de imprimir</li>
                  <li>SVG é ideal para materiais grandes</li>
                </ul>
              </div>

              <div className="soft-card">
                <div className="soft-card-title">Formato recomendado</div>
                <div className="format-list">
                  <div className="format-row">
                    <span className="format-pill">Redes sociais</span>
                    <strong>PNG</strong>
                  </div>
                  <div className="format-row">
                    <span className="format-pill">Impressão</span>
                    <strong>SVG</strong>
                  </div>
                  <div className="format-row">
                    <span className="format-pill">Catálogos</span>
                    <strong>JPG</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <aside className="stack">
          <section className="panel">
            <div className="panel-heading">
              <ImagePlus className="panel-icon" />
              <div>
                <h2>Logo no centro</h2>
                <p>Envie a marca da empresa ou use o ícone do Instagram.</p>
              </div>
            </div>

            <div className="logo-actions">
              <label className="upload-btn">
                Upload de logo
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                />
              </label>

              <button
                className={`secondary-btn ${isInstagramLogo ? "selected" : ""}`}
                onClick={() => setIsInstagramLogo(true)}
              >
                <ImagePlus size={16} />
                Usar Instagram
              </button>

              <button
                className="secondary-btn"
                onClick={() => {
                  setLogoDataUrl(null);
                  setLogoName("");
                  setIsInstagramLogo(false);
                }}
              >
                Remover centro
              </button>
            </div>
            <p className="logo-warning">
              A logo é limitada automaticamente a uma área menor do centro.
              Arquivos com muito texto, baixo contraste ou detalhes finos ainda
              podem dificultar a leitura em algumas câmeras.
            </p>

            <p className="helper-text">
              {isInstagramLogo
                ? "Ícone do Instagram aplicado no centro do QR."
                : logoName
                  ? `Logo selecionada: ${logoName}`
                  : "Nenhuma logo enviada ainda."}
            </p>
          </section>

          <section className="panel">
            <div className="panel-heading">
              <Sparkles className="panel-icon" />
              <div>
                <h2>Estilo visual</h2>
                <p>Use o mesmo padrão elegante do projeto principal.</p>
              </div>
            </div>

            <div className="field-grid compact">
              <label>
                Cor principal
                <input
                  type="color"
                  value={style.foreground}
                  onChange={(event) =>
                    setStyle((current) => ({
                      ...current,
                      foreground: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Fundo
                <input
                  type="color"
                  value={style.background}
                  onChange={(event) =>
                    setStyle((current) => ({
                      ...current,
                      background: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Pontos
                <select
                  value={style.dotStyle}
                  onChange={(event) =>
                    setStyle((current) => ({
                      ...current,
                      dotStyle: event.target.value as DotStyle,
                    }))
                  }
                >
                  <option value="rounded">Rounded</option>
                  <option value="dots">Dots</option>
                  <option value="classy-rounded">Classy Rounded</option>
                  <option value="classy">Classy</option>
                  <option value="extra-rounded">Extra Rounded</option>
                  <option value="square">Square</option>
                </select>
              </label>
              <label>
                Moldura
                <select
                  value={style.cornerSquareStyle}
                  onChange={(event) =>
                    setStyle((current) => ({
                      ...current,
                      cornerSquareStyle: event.target
                        .value as CornerSquareStyle,
                    }))
                  }
                >
                  <option value="extra-rounded">Extra Rounded</option>
                  <option value="square">Square</option>
                  <option value="dot">Dot</option>
                </select>
              </label>
            </div>
          </section>

          <section className="panel">
            <div className="panel-heading">
              <Download className="panel-icon" />
              <div>
                <h2>Formatos de download</h2>
                <p>
                  Exporte para redes sociais, materiais impressos e vetores.
                </p>
              </div>
            </div>

            <div className="download-grid">
              {(["png", "jpeg", "svg"] as DownloadFormat[]).map((extension) => (
                <button
                  key={extension}
                  className="download-btn"
                  onClick={() => handleDownload(extension)}
                  disabled={isDownloading !== null}
                >
                  <Download size={16} />
                  {isDownloading === extension
                    ? `Gerando ${extension === "jpeg" ? "JPG" : extension.toUpperCase()}...`
                    : `Baixar ${extension === "jpeg" ? "JPG" : extension.toUpperCase()}`}
                </button>
              ))}
            </div>
          </section>
        </aside>
      </main>

      <section className="seo-shell">
        <div className="seo-section">
          <h2>Como gerar seu QR Code personalizado?</h2>
          <p>
            O QRForge converte o conteúdo que você digita (como links, textos,
            dados de Wi‑Fi ou payload Pix) em um padrão de módulos binários
            organizado em uma grade. Esse padrão é o que as câmeras conseguem
            interpretar rapidamente para abrir o destino correto. Você ainda pode
            aplicar cores, molduras e uma logo central — mantendo correção de
            erro alta para preservar a leitura.
          </p>
        </div>

        <div className="seo-section">
          <h2>Gerador de QR Code com Privacidade Total.</h2>
          <p>
            Diferente de outros geradores online, o QRForge processa todas as
            informações localmente no seu navegador. Isso significa que seus
            links, senhas de Wi‑Fi ou dados inseridos no QR Code não precisam ser
            enviados para nossos servidores. Seus dados permanecem com você.
          </p>
        </div>

        <div className="seo-section">
          <h2>Qual formato escolher: PNG ou SVG?</h2>
          <div className="table-shell" role="region" aria-label="Tabela de formatos">
            <table className="format-table">
              <thead>
                <tr>
                  <th>Formato</th>
                  <th>Uso recomendado</th>
                  <th>Vantagem</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>PNG</strong>
                  </td>
                  <td>Redes sociais e web</td>
                  <td>Compatibilidade universal.</td>
                </tr>
                <tr>
                  <td>
                    <strong>SVG</strong>
                  </td>
                  <td>Impressão e design</td>
                  <td>Vetorial, não perde qualidade ao aumentar.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="seo-section">
          <h2>Por que usar um gerador de QR Code com logo?</h2>
          <p>
            Um QR Code personalizado transmite mais confiança e aumenta a chance
            de escaneamento. Ao inserir a logo da empresa, ajustar cores da
            marca e escolher um estilo visual coerente, o código deixa de
            parecer genérico e passa a funcionar como peça de branding.
          </p>
        </div>

        <div className="feature-grid">
          <article className="feature-card">
            <Zap className="feature-icon" />
            <h3>Presets rápidos</h3>
            <p>
              Aplique estilos prontos para WhatsApp, Wi-Fi e Pix com um clique.
            </p>
          </article>
          <article className="feature-card">
            <ShieldCheck className="feature-icon" />
            <h3>Privacidade local</h3>
            <p>
              Os dados são tratados no navegador, sem depender de upload
              externo.
            </p>
          </article>
          <article className="feature-card">
            <Wifi className="feature-icon" />
            <h3>Casos de uso reais</h3>
            <p>
              Ideal para cardápios, checkout Pix, perfis sociais e materiais
              impressos.
            </p>
          </article>
        </div>

        <div className="seo-section">
          <h2>Como criar um QR Code profissional em poucos passos</h2>
          <ol className="how-to-list">
            <li>
              Escolha o tipo de conteúdo: link, texto, WhatsApp, Wi-Fi ou Pix.
            </li>
            <li>
              Preencha os dados principais e aplique um preset se quiser
              acelerar.
            </li>
            <li>
              Personalize cores, estilo dos pontos e adicione uma logo central.
            </li>
            <li>Baixe em PNG, JPG ou SVG para usar na web ou na impressão.</li>
          </ol>
        </div>

        <div className="seo-section">
          <h2>FAQ sobre QR Code com logo, Pix e SVG</h2>
          <div className="faq-list">
            {FAQ_ITEMS.map((item) => (
              <details key={item.question} className="faq-item">
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>

        <div className="seo-section">
          <h2>Leitura recomendada</h2>
          <p className="blog-cta">
            Publicamos um artigo completo sobre usos práticos, pagamentos e
            marketing.
          </p>
          <button className="secondary-btn" onClick={() => setCurrentView("blogQr2026")}>
            Ler: A importância dos QR Codes em 2026
          </button>
        </div>
      </section>
    </>
  );

  const content = LEGAL_CONTENT[currentView as keyof typeof LEGAL_CONTENT];

  return (
    <div className="app-shell">
      <header className="site-header">
        <button className="brand-mark" onClick={() => setCurrentView("home")}>
          <img
            src="/logo.webp"
            alt={`${SITE_NAME} logo`}
            className="brand-logo"
            width={58}
            height={58}
          />
        </button>

        <nav className="top-nav">
          <button
            onClick={() => setCurrentView("home")}
            className={currentView === "home" ? "active-nav" : ""}
            aria-label="Ir para o Gerador de QR Code"
          >
            Gerador
          </button>
          <button
            onClick={() => setCurrentView("about")}
            className={currentView === "about" ? "active-nav" : ""}
            aria-label="Sobre o projeto"
          >
            Sobre
          </button>
          <button
            onClick={() => setCurrentView("privacy")}
            className={currentView === "privacy" ? "active-nav" : ""}
            aria-label="Política de Privacidade"
          >
            Privacidade
          </button>
          <button
            onClick={() => setCurrentView("privacyTech")}
            className={currentView === "privacyTech" ? "active-nav" : ""}
            aria-label="Por que a Privacidade Importa"
          >
            Privacidade Técnica
          </button>
          <button
            onClick={() => setCurrentView("terms")}
            className={currentView === "terms" ? "active-nav" : ""}
            aria-label="Termos de Uso"
          >
            Termos
          </button>
        </nav>
      </header>

      {currentView === "home" ? (
        renderHome()
      ) : (
        <section className="legal-view-container">
          <button className="back-btn" onClick={() => setCurrentView("home")}>
            <ArrowLeft size={18} />
            Voltar para o gerador
          </button>
          <div className="legal-card">
            <h1>{content.title}</h1>
            <div
              className="legal-body"
              dangerouslySetInnerHTML={{ __html: content.content }}
            />
          </div>
        </section>
      )}

      <footer className="site-footer">
        <div className="footer-links">
          <span>{SITE_NAME} © 2026</span>
          <button onClick={() => setCurrentView("about")}>Sobre</button>
          <button onClick={() => setCurrentView("privacy")}>Privacidade</button>
          <button onClick={() => setCurrentView("privacyTech")}>
            Privacidade Técnica
          </button>
          <button onClick={() => setCurrentView("terms")}>Termos</button>
        </div>
        <p>Gerador de QR Code com foco em design, SEO e privacidade local.</p>
      </footer>
    </div>
  );
}

function renderFields(
  form: QrFormState,
  setForm: Dispatch<SetStateAction<QrFormState>>,
) {
  if (form.mode === "url") {
    return (
      <div className="field-stack">
        <label>
          Link de destino
          <input
            type="url"
            value={form.url}
            onChange={(event) =>
              setForm((current) => ({ ...current, url: event.target.value }))
            }
            placeholder="https://seusite.com.br"
          />
        </label>
      </div>
    );
  }

  if (form.mode === "text") {
    return (
      <div className="field-stack">
        <label>
          Texto livre
          <textarea
            rows={5}
            value={form.text}
            onChange={(event) =>
              setForm((current) => ({ ...current, text: event.target.value }))
            }
            placeholder="Digite a mensagem que o QR Code deve abrir."
          />
        </label>
      </div>
    );
  }

  if (form.mode === "whatsapp") {
    return (
      <div className="field-grid">
        <label>
          Número com DDI
          <input
            type="text"
            value={form.whatsappPhone}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                whatsappPhone: event.target.value.replace(/\D/g, ""),
              }))
            }
            placeholder="5511999999999"
          />
        </label>
        <label className="full-width">
          Mensagem inicial
          <textarea
            rows={4}
            value={form.whatsappMessage}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                whatsappMessage: event.target.value,
              }))
            }
            placeholder="Olá! Quero saber mais."
          />
        </label>
      </div>
    );
  }

  if (form.mode === "wifi") {
    return (
      <div className="field-grid">
        <label>
          Nome da rede
          <input
            type="text"
            value={form.wifiSsid}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                wifiSsid: event.target.value,
              }))
            }
            placeholder="Minha Rede"
          />
        </label>
        <label>
          Segurança
          <select
            value={form.wifiSecurity}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                wifiSecurity: event.target.value as WifiSecurity,
              }))
            }
          >
            <option value="WPA">WPA/WPA2</option>
            <option value="WEP">WEP</option>
            <option value="nopass">Sem senha</option>
          </select>
        </label>
        <label>
          Senha
          <input
            type="text"
            value={form.wifiPassword}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                wifiPassword: event.target.value,
              }))
            }
            placeholder="senha12345"
          />
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={form.wifiHidden}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                wifiHidden: event.target.checked,
              }))
            }
          />
          Rede oculta
        </label>
      </div>
    );
  }

  return (
    <div className="field-grid">
      <label>
        Chave Pix
        <input
          type="text"
          value={form.pixKey}
          onChange={(event) =>
            setForm((current) => ({ ...current, pixKey: event.target.value }))
          }
          placeholder="email, telefone, CPF/CNPJ ou aleatória"
        />
      </label>
      <label>
        Nome do recebedor
        <input
          type="text"
          value={form.pixName}
          onChange={(event) =>
            setForm((current) => ({ ...current, pixName: event.target.value }))
          }
          placeholder="SUA EMPRESA"
        />
      </label>
      <label>
        Cidade
        <input
          type="text"
          value={form.pixCity}
          onChange={(event) =>
            setForm((current) => ({ ...current, pixCity: event.target.value }))
          }
          placeholder="SAO PAULO"
        />
      </label>
      <label>
        Valor opcional
        <input
          type="text"
          value={form.pixAmount}
          onChange={(event) =>
            setForm((current) => ({
              ...current,
              pixAmount: event.target.value.replace(",", "."),
            }))
          }
          placeholder="49.90"
        />
      </label>
      <label className="full-width">
        Descrição
        <input
          type="text"
          value={form.pixDescription}
          onChange={(event) =>
            setForm((current) => ({
              ...current,
              pixDescription: event.target.value,
            }))
          }
          placeholder="Pagamento de pedido"
        />
      </label>
      <label>
        TXID
        <input
          type="text"
          value={form.pixTxid}
          onChange={(event) =>
            setForm((current) => ({ ...current, pixTxid: event.target.value }))
          }
          placeholder="PEDIDO123"
        />
      </label>
    </div>
  );
}

function modeLabel(mode: ContentMode) {
  return {
    url: "Link",
    text: "Texto",
    whatsapp: "WhatsApp",
    wifi: "Wi-Fi",
    pix: "Pix",
  }[mode];
}

function buildQrPayload(form: QrFormState) {
  if (form.mode === "url") return form.url.trim() || "https://seusite.com.br";
  if (form.mode === "text")
    return form.text.trim() || "Digite um texto para gerar o QR Code.";
  if (form.mode === "whatsapp") {
    const phone = form.whatsappPhone.replace(/\D/g, "");
    const message = encodeURIComponent(form.whatsappMessage.trim());
    return `https://wa.me/${phone || "5511999999999"}${message ? `?text=${message}` : ""}`;
  }
  if (form.mode === "wifi") {
    const ssid = escapeWifiValue(form.wifiSsid || "MinhaRedeWiFi");
    const password = escapeWifiValue(form.wifiPassword);
    const hidden = form.wifiHidden ? "true" : "false";
    return `WIFI:T:${form.wifiSecurity};S:${ssid};P:${password};H:${hidden};;`;
  }
  return buildPixPayload({
    key: form.pixKey || "contato@suaempresa.com.br",
    name: form.pixName || "SUA EMPRESA",
    city: form.pixCity || "SAO PAULO",
    amount: normalizePixAmount(form.pixAmount),
    description: form.pixDescription,
    txid: form.pixTxid || "***",
  });
}

function buildPixPayload({
  key,
  name,
  city,
  amount,
  description,
  txid,
}: {
  key: string;
  name: string;
  city: string;
  amount: string;
  description: string;
  txid: string;
}) {
  const merchantAccount = payloadField(
    "26",
    payloadField("00", "br.gov.bcb.pix") +
      payloadField("01", key.trim()) +
      (description.trim()
        ? payloadField("02", trimValue(description, 50))
        : ""),
  );
  const payload =
    payloadField("00", "01") +
    payloadField("01", "12") +
    merchantAccount +
    payloadField("52", "0000") +
    payloadField("53", "986") +
    (amount ? payloadField("54", amount) : "") +
    payloadField("58", "BR") +
    payloadField("59", trimValue(normalizePixText(name), 25) || "RECEBEDOR") +
    payloadField("60", trimValue(normalizePixText(city), 15) || "SAO PAULO") +
    payloadField("62", payloadField("05", trimValue(txid || "***", 25))) +
    "6304";
  return `${payload}${crc16(payload)}`;
}

function payloadField(id: string, value: string) {
  return `${id}${value.length.toString().padStart(2, "0")}${value}`;
}

function crc16(payload: string) {
  let result = 0xffff;
  for (let offset = 0; offset < payload.length; offset += 1) {
    result ^= payload.charCodeAt(offset) << 8;
    for (let bit = 0; bit < 8; bit += 1) {
      result = (result & 0x8000) !== 0 ? (result << 1) ^ 0x1021 : result << 1;
      result &= 0xffff;
    }
  }
  return result.toString(16).toUpperCase().padStart(4, "0");
}

function normalizePixText(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9 ]/g, "")
    .toUpperCase();
}

function normalizePixAmount(value: string) {
  const normalized = value.trim().replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed.toFixed(2) : "";
}

function trimValue(value: string, maxLength: number) {
  return value.trim().slice(0, maxLength);
}

function escapeWifiValue(value: string) {
  return value.replace(/([\\;,:"])/g, "\\$1");
}

function encodeSvgDataUrl(svg: string) {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function upsertMetaTag(name: string, content: string) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertMetaProperty(property: string, content: string) {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertLinkTag(rel: string, href: string) {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", rel);
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default App;
